/**
 * View component for /admin/authoring/studies/:studyId/study-users/:studyUserId/update
 *
 * Updates a single studyUser from a copy of the selcted studyUser
 * as defined in the studyUser reducer
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { history, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import third-party libraries
import _ from 'lodash';
import { DateTime } from 'luxon';

// import actions
import * as courseActions from '../../course/courseActions';
import * as siteActions from '../../site/siteActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as studyUserActions from '../studyUserActions';
import * as studyRoleActions from '../../studyRole/studyRoleActions';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';

// import module components
import StudyUserForm from '../components/StudyUserForm.js.jsx';

class AdminUpdateStudyUser extends Base {
  constructor(props) {
    super(props);
    const { selectedStudyUser, studyUserStore } = this.props;
    this.state = {
      alertMessage: false
      , formHelpers: {
        doesExpire: 'no'
        , expires: DateTime.local().plus({years: 2}).valueOf()
        , linkSiteFrom: 'freetext'
      }
      , studyUser: studyUserStore.byId[selectedStudyUser.id] ? _.cloneDeep(studyUserStore.byId[selectedStudyUser.id]) : {}
      // NOTE: we don't want to change the store, just make changes to a copy
    }
    this._bind(
      '_handleFormChange'
      , '_handleFormSubmit'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(studyUserActions.fetchSingleIfNeeded(match.params.studyUserId)).then((action) => {
      dispatch(courseActions.fetchListIfNeeded('_group', action.item._group));
      dispatch(sponsorActions.fetchSingleIfNeeded(action.item._organization));
      dispatch(siteActions.fetchListIfNeeded('_group', action.item._group));
      dispatch(studyActions.fetchSingleIfNeeded(action.item._group));
      dispatch(studyRoleActions.fetchListIfNeeded('study', action.item._group));
    });
  }

  componentWillReceiveProps(nextProps) {
    const { match, selectedStudyUser, siteStore, studyUserStore } = nextProps;
    const studyUser = studyUserStore.byId[selectedStudyUser.id];
    const studySites = siteStore.util.getList('_group', match.params.studyId);
    let formHelpers = {...this.state.formHelpers}
    formHelpers.doesExpire = studyUser && studyUser.expires ? 'yes' : 'no';
    formHelpers.expires = studyUser && studyUser.expires ? DateTime.fromISO(studyUser.expires).valueOf() : DateTime.local().plus({years: 2}).valueOf()
    formHelpers.linkSiteFrom = studySites && studyUser && studyUser.siteNumber && (_.findIndex(studySites, {'number': studyUser.siteNumber}) > -1) ? 'sites' : 'freetext';
    this.setState({
      formHelpers
      , studyUser: studyUserStore.byId[selectedStudyUser.id] ? _.cloneDeep(studyUserStore.byId[selectedStudyUser.id]) : {}
      //we don't want to actually change the store's studyUser, just use a copy
    })

  }

  _handleFormChange(e) {
    const { selectedStudyUser, studyUserStore } = this.props;
    let newState = _.update( this.state, e.target.name, () => {
      return e.target.value;
    });
    const originalStudyUser = studyUserStore.byId[selectedStudyUser.id];
    if(this.state.studyUser._course !== originalStudyUser._course) {
      newState.alertMessage = true;
    }
    this.setState(newState);
  }

  _handleFormSubmit(e) {
    const { dispatch, loggedInUser, history, match, studyRoleStore } = this.props;
    e.preventDefault();
    let updatedStudyUser = {...this.state.studyUser};
    updatedStudyUser._lastUpdateBy = loggedInUser._id;
    if(this.state.formHelpers.doesExpire === 'yes') {
      updatedStudyUser.expires = new Date(this.state.formHelpers.expires);
    } else {
      updatedStudyUser.expires = null;
    }
    if(updatedStudyUser.status !== 'active') {
      updatedStudyUser.dateRevoked = new Date();
      updatedStudyUser._revokedBy = loggedInUser._id;
    }

    // 1.12 update the role string for backwards compatibility
    updatedStudyUser.role = studyRoleStore.byId[updatedStudyUser._groupRole].role;

    dispatch(studyUserActions.sendUpdateStudyUser(updatedStudyUser)).then((action) => {
      if(action.success) {
        dispatch(studyUserActions.invalidateList("_group", match.params.studyId));
        history.push(`/admin/authoring/studies/${match.params.studyId}/study-users`)
      } else {
        // console.log("Response Error:");
        // console.log(action);
        alert(`ERROR: ${action.error}`);
      }
    });
  }

  render() {
    const {
      courseStore
      , location
      , loggedInUser
      , match
      , selectedStudyUser
      , siteStore
      , sponsorStore
      , studyStore
      , studyUserStore
      , studyRoleStore
    } = this.props;

    const { formHelpers, studyUser } = this.state;
    const selectedSponsor = sponsorStore.selected.getItem() || {};
    const selectedStudy = studyStore.selected.getItem() || {};
    const isEmpty = (!studyUser || studyUser._id === null || studyUser._id === undefined);
    const availableCourses = courseStore.util.getList('_group', match.params.studyId);
    const studyRoles = studyRoleStore.util.getList('study', match.params.studyId);

    let studySites = [];
    if(selectedStudy && selectedStudy._id) {
      studySites = siteStore.util.getList('_group', match.params.studyId);
    }
    return  (
      <AuthoringLayout>
        <Helmet
          title={"Update User | Pro-ficiency"}
        />
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
            </div>
            { isEmpty ?
              (selectedStudyUser.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              <StudyUserForm
                loggedInUser={loggedInUser}
                availableCourses={availableCourses}
                // availableRoles={studyStore ? studyUserStore.availableRoles : []}
                courseMap={courseStore.byId}
                cancelLink={`${match.url.replace('/update-user','')}`}
                formHelpers={formHelpers}
                formTitle="Update Program Permission"
                formType="update"
                handleFormChange={this._handleFormChange}
                handleFormSubmit={this._handleFormSubmit}
                onSelectChange={this._onSelectChange}
                sponsor={selectedSponsor}
                statuses={studyUserStore ? studyUserStore.statuses : []}
                study={selectedStudy}
                studySites={studySites}
                studyUser={studyUser}
                studyRoles={studyRoles}
                viewingAs={'admin'}
              />
            }
          </section>
          <AlertModal
            alertMessage={<div><strong>CAUTION: </strong> Are you <em>sure</em> you want to change this user's assigned course?  <br/><br/> FYI, this may skew the completion analytics pages. </div>}
            alertTitle="Confirm course change"
            closeAction={() => this.setState({alertMessage: false})}
            confirmAction={() => this.setState({alertMessage: false})}
            confirmText={"I understand what I'm doing"}
            isOpen={this.state.alertMessage}
            type="danger"
          />
        </div>
      </AuthoringLayout>
    )
  }
}

AdminUpdateStudyUser.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    courseStore: store.course
    , loggedInUser: store.user.loggedIn.user
    , selectedStudyUser: store.studyUser.selected
    , siteStore: store.site
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , studyUserStore: store.studyUser
    , studyRoleStore: store.studyRole
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminUpdateStudyUser)
);



// WEBPACK FOOTER //
// modules/studyUser/views/AdminUpdateStudyUser.js.jsx